"use client";

import React, { useState, useRef, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";

const AboutDoctor: React.FC = () => {
  const [imageInView, setImageInView] = useState(false);
  const imageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setImageInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    if (imageRef.current) {
      observer.observe(imageRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      className="w-full bg-white py-12"
      role="region"
      aria-labelledby="about-doctor-heading"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <div className="flex flex-col md:flex-row gap-8 md:items-stretch">
          {/* Image - lazy load when in viewport, fills column height on desktop */}
          <div
            ref={imageRef}
            className="w-full md:w-1/2 relative h-[300px] sm:h-[400px] md:h-auto md:min-h-[500px] rounded-lg overflow-hidden"
          >
            {imageInView ? (
              <Image
                src="/images/professional-portrait-of-best-dentist-in-astoria-dr-iosif-khaimov-smiling-and-sitting-in-office-chair-wearing-white-coat-and-yellow-tie.webp"
                alt="professional-portrait-of-best-dentist-in-astoria-dr-iosif-khaimov-smiling-and-sitting-in-office-chair-wearing-white-coat-and-yellow-tie"
                fill
                className="object-cover object-top"
                sizes="(max-width: 480px) 100vw, (max-width: 768px) 90vw, 50vw"
                quality={75}
                priority={false}
              />
            ) : (
              <div className="w-full h-full bg-gray-200 animate-pulse flex items-center justify-center">
                <div className="flex flex-col items-center space-y-2">
                  <div className="w-16 h-16 bg-gray-300 rounded-full"></div>
                  <div className="w-24 h-4 bg-gray-300 rounded"></div>
                </div>
              </div>
            )}
          </div>

          {/* Text and Button */}
          <div className="w-full md:w-1/2 flex flex-col justify-center items-center md:items-start text-center md:text-left p-4 sm:p-8">
            <h2
              id="about-doctor-heading"
              className="
                text-3xl sm:text-4xl lg:text-5xl font-semibold text-gray-900
                after:content-[''] after:block after:w-[200px] after:h-[3px] after:mx-auto md:after:ml-0 after:my-5
                after:bg-[linear-gradient(to_right,#AE7D2F_0%,#ECD05B_50%,#AE7D2F_100%)]
              "
            >
              Meet Dr. Iosif Khaimov, Your Astoria Dentist
            </h2>

            <p className="text-base sm:text-lg lg:text-xl text-gray-700 leading-relaxed mb-4 mx-auto md:mx-0">
              A graduate of New York University&apos;s College of Dentistry, Dr.
              Iosif Khaimov has spent over seven years delivering exceptional
              dental care across New York City &mdash; and now brings that
              expertise home to Astoria, Queens as the founder of Smile4Me
              Dental Care.
            </p>

            <p className="text-base sm:text-lg lg:text-xl text-gray-700 leading-relaxed mb-4 mx-auto md:mx-0">
              Dr. Khaimov&apos;s experience spans dental implants, Invisalign,
              cosmetic dentistry, crowns, and same-day emergency care. Every
              treatment plan is tailored to the individual, backed by a
              commitment to ongoing education that keeps his patients at the
              forefront of modern dentistry.
            </p>

            <p className="text-base sm:text-lg lg:text-xl italic text-[#d5ac26] bg-gray-50 p-4 rounded-md mx-auto md:mx-0">
              &quot;I am committed to providing each patient with personalized
              care that is tailored to their unique needs and concerns. At
              Smile4Me, we use the latest in dental technology to help create
              your dream smile.&quot; &mdash; Dr. Iosif Khaimov
            </p>

            <div className="mt-6 mx-auto md:mx-0">
              <Link
                href="/meet-your-dentist"
                className="
                  uppercase font-bold text-lg tracking-wide text-white
                  px-6 py-2
                  bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F]
                  hover:from-[#ECD05B] hover:via-[#AE7D2F] hover:to-[#AE7D2F]
                  rounded-lg
                  shadow-lg hover:shadow-inner
                  [text-shadow:1px_1px_2px_rgba(0,0,0,0.5)]
                  hover:scale-105
                  transition-all duration-300 ease-in-out
                "
                aria-label="Learn more about Dr. Iosif Khaimov"
              >
                Meet Dr. Khaimov
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutDoctor;